import { useEffect, useState } from 'react';
import { fetchAuditLog, type AuditEvent } from '../api/client.js';
import { isMachineActor, MachineActorBadge, MachineAuthorityLine } from './MachineActorBadge.js';

export function AuditLogPage() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void fetchAuditLog(200)
      .then(setEvents)
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : 'Failed to load audit log'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="grid min-h-[18rem] place-items-center text-sm text-slate-500 animate-pulse">Loading audit log...</div>;
  }

  if (error) {
    return <div className="grid min-h-[18rem] place-items-center rounded-xl border border-rose-200 bg-rose-50 text-sm text-rose-800">{error}</div>;
  }

  return (
    <main className="flex-1 overflow-y-auto bg-background-light">
      <div className="p-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audit Log</h1>
          <p className="text-sm text-slate-500">{events.length} events (most recent first)</p>
        </div>

        <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left">Timestamp</th>
                <th className="px-5 py-3 text-left">Action</th>
                <th className="px-5 py-3 text-left">Actor</th>
                <th className="px-5 py-3 text-left">Target</th>
                <th className="px-5 py-3 text-left">Course</th>
                <th className="px-5 py-3 text-left">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {events.map((event) => (
                <tr className="transition-colors hover:bg-slate-50" key={event.eventId}>
                  <td className="whitespace-nowrap px-5 py-3 font-mono text-xs text-slate-500">
                    {formatTimestamp(event.timestamp)}
                  </td>
                  <td className="px-5 py-3">
                    <span className={actionBadgeClass(event.action)}>
                      {event.action.replaceAll('_', ' ')}
                    </span>
                  </td>
                  {/* The Actor column is the whole point of an audit trail. A bulk run writes rows
                      under the operator's identity, so without the badge every one of them asserts
                      that a named person performed it. The address stays — it is who triggered the
                      run — and the badge plus authority say it was not an individual human act. */}
                  <td className="px-5 py-3 text-slate-700" data-testid={`audit-actor-${event.eventId}`}>
                    <span className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="text-sm">{event.userEmail ?? event.userId}</span>
                      {isMachineActor(event) ? (
                        <MachineActorBadge authority={event.actorAuthority} testId="audit-machine-actor" />
                      ) : null}
                    </span>
                    {isMachineActor(event) ? <MachineAuthorityLine authority={event.actorAuthority} /> : null}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-500">
                    {event.workItemId ?? event.waveId ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-slate-500">{event.courseKey}</td>
                  <td className="max-w-[200px] truncate px-5 py-3 text-xs text-slate-400">
                    {event.details ? JSON.stringify(event.details) : '—'}
                  </td>
                </tr>
              ))}
              {events.length === 0 ? (
                <tr>
                  <td className="px-5 py-8 text-center text-slate-400" colSpan={6}>
                    No audit events recorded yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  } catch {
    return iso;
  }
}

function actionBadgeClass(action: string): string {
  if (action.includes('approve') || action.includes('execute')) {
    return 'rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700';
  }
  if (action.includes('rollback') || action.includes('cancel')) {
    return 'rounded bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-600';
  }
  if (action.includes('create') || action.includes('import')) {
    return 'rounded bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-700';
  }
  return 'rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600';
}
